import {createReducer} from "redux-act";
import {omit} from "lodash";
import {
	clearFormError,
	clearUserState,
	requestProfile,
	requestProfileFailed,
	requestProfileSuccess,
	requestUser,
	requestUserFailed,
	requestUserSuccess,
	saveProfile,
	saveProfileFailed,
	saveProfileSuccess,
	setFormError,
} from "modules/actions";
import {RequestStateType} from "modules/enums";
import {IFormError} from "modules/types";

export interface IUser {
	id: number;
	email: string;
	password: string;
	firstName: string;
	lastName: string;
	favoriteTeam: number;
	username: string;
	lang: string;
	country: string;
	locationCode: string;
	isNotificationsEnabled: boolean;
	termsGZ: boolean;
	optInGZ: boolean;
	avatar: string | null;
}

export interface IProfile {
	terms: boolean | null;
	isNotificationsEnabled: boolean | null;
	acceptedAt: string | null;
	hasSeenChampionLeagueAddingModal: null | boolean;
	hasSeenChampionLeagueNextRoundModal: null | boolean;
	hasSeenChampionLeagueRemovingModal: null | boolean;
	hasSeenChampionLeagueWinnerModal: null | boolean;
	showPrompt: boolean;
}

export interface IUserReducer {
	user: IUser | null;
	profile: IProfile | null;
	requestState: RequestStateType;
	profileRequestState: RequestStateType;
	sessionCheckedState: RequestStateType;
	saveProfileRequestState: RequestStateType;
	errors: IFormError;
}

const initialState: IUserReducer = {
	user: null,
	profile: null,
	requestState: RequestStateType.Idle,
	profileRequestState: RequestStateType.Idle,
	saveProfileRequestState: RequestStateType.Idle,
	sessionCheckedState: RequestStateType.Idle,
	errors: {},
};

const onRequestUser = (state: IUserReducer) => ({
	...state,
	sessionCheckedState: RequestStateType.Loading,
});

const onRequestUserSuccess = (state: IUserReducer, user: IUser) => ({
	...state,
	user,
	sessionCheckedState: RequestStateType.Success,
});

const onRequestUserFailed = (state: IUserReducer) => ({
	...state,
	sessionCheckedState: RequestStateType.Error,
});

const onRequestProfile = (state: IUserReducer) => ({
	...state,
	profileRequestState: RequestStateType.Loading,
});

const onRequestProfileSuccess = (state: IUserReducer, payload: IProfile) => ({
	...state,
	profileRequestState: RequestStateType.Success,
	profile: payload,
});

const onRequestProfileFailed = (state: IUserReducer) => ({
	...state,
	profileRequestState: RequestStateType.Error,
});

const onSaveProfile = (state: IUserReducer) => ({
	...state,
	saveProfileRequestState: RequestStateType.Loading,
});

const onSaveProfileSuccess = (state: IUserReducer, payload: IProfile) => ({
	...state,
	saveProfileRequestState: RequestStateType.Success,
	profile: payload,
});

const onSaveProfileFailed = (state: IUserReducer, payload: IFormError) => ({
	...state,
	saveProfileRequestState: RequestStateType.Error,
	errors: payload,
});

const onSetFromError = (state: IUserReducer, errors: IFormError) => ({
	...state,
	errors,
});

const onClearFormError = (state: IUserReducer, payload: string | void) => ({
	...state,
	errors: payload ? omit(state.errors, [payload]) : {},
});

const onClearUserState = () => ({...initialState});

export const userReducer = createReducer<IUserReducer>({}, initialState)
	/**
	 * get User
	 * */
	.on(requestUser, onRequestUser)
	.on(requestUserSuccess, onRequestUserSuccess)
	.on(requestUserFailed, onRequestUserFailed)
	/**
	 * get Profile
	 */
	.on(requestProfile, onRequestProfile)
	.on(requestProfileSuccess, onRequestProfileSuccess)
	.on(requestProfileFailed, onRequestProfileFailed)
	/**
	 * save Profile
	 */
	.on(saveProfile, onSaveProfile)
	.on(saveProfileSuccess, onSaveProfileSuccess)
	.on(saveProfileFailed, onSaveProfileFailed)
	/**
	 * Error
	 */
	.on(setFormError, onSetFromError)
	.on(clearFormError, onClearFormError)
	/**
	 * Clear state
	 * */
	.on(clearUserState, onClearUserState);
